import { floorplannerStore } from "../../store/floorplannerStore";
import { renderStore } from "../../store/renderStore";
import { editorStore } from "../../store/editorStore";
import {
  WallConnectionEnd,
  WallConnectionStart,
  WallConnectionMiddle,
  WallShapeType,
  WallCornerKey,
  WallConnectionType,
  ClippingPolygonType,
} from "../../types/floorplannerTypes";
import { checkLineIntersection } from "./checkLineIntersections";
import * as THREE from "three";
import { otherModifiedPath } from '../../types/floorplannerTypes';
import { WallGeometryCache } from '../../utils/WallGeometryCache';
import { getCornerReference, populateDefaultCorners, updatePathPoint } from "../../utils/wallCornerHelpers";
import { assertValidWallGeometry, canValidateRectangularWall } from "../../utils/wallGeometryValidation";
import { isEndpointConnectionPosition, calculateWallEdgePoint, getWallAngle } from "../../utils/edgeConnectionHelpers";
import { getConnectionPoint } from "../../utils/connectionGeometry";
import { calculateWallImaginaryLines, intersectImaginaryLines, getEndpointImaginaryIntersections } from "../../utils/imaginaryLineHelpers";

/**
 * Deduplicate "missing connected shape" warnings during batch rebuilds.
 * Large paste/drag operations hit this path hundreds of times for the same pairs.
 */
const missingConnectedShapeWarnKeys = new Set<string>();
const MISSING_CONNECTED_SHAPE_WARN_CAP = 200;

const warnMissingConnectedShapeOnce = (wallId: string, connectionId: string): void => {
  const key = `${wallId}->${connectionId}`;
  if (missingConnectedShapeWarnKeys.has(key)) {
    return;
  }
  if (missingConnectedShapeWarnKeys.size >= MISSING_CONNECTED_SHAPE_WARN_CAP) {
    missingConnectedShapeWarnKeys.clear();
  }
  missingConnectedShapeWarnKeys.add(key);
  console.warn(
    `    Connected wall shape not found for connection ${connectionId}, adding forward clipping only`,
  );
};

const getCornerOfShape = (
  shape: WallShapeType,
  position: WallCornerKey,
): {
  position: THREE.Vector2;
  index: number;
} => {
  const reference = getCornerReference(shape, position);
  const index = reference.indices.length > 0 ? reference.indices[0] : -1;
  let point: THREE.Vector2 | undefined;
  if (index >= 0 && index < shape.paths.length) {
    point = shape.paths[index];
  } else if (reference.point) {
    point = reference.point;
  } else {
    point = shape.paths[0] ?? new THREE.Vector2();
  }

  return {
    position: point,
    index,
  };
};

/**
 * Set corner position while maintaining wall geometry constraints.
 * CRITICAL: When moving start endpoint corners (topLeft/topRight), we must maintain wall width.
 * This means both corners must move together to preserve the perpendicular distance.
 *
 * In edge mode (useEdgeConnections = true), wall width maintenance is skipped because
 * walls are always 4-point rectangles and joint polygons handle visual adjustments.
 */
const setCornerPosition = (
  shape: WallShapeType,
  corner: WallCornerKey,
  point: THREE.Vector2,
  tracking?: {
    wallId: string;
    collection: otherModifiedPath[];
  },
): void => {
  const { index } = getCornerOfShape(shape, corner);
  if (index < 0) {
    return;
  }

  const shouldMaintainWidth =
    !shape.lineSide || shape.lineSide === "inner" || shape.lineSide === "endcap";

  // Update the primary corner
  updatePathPoint(shape, index, point);

  // Skip wall width maintenance for endpoint connections
  // Endpoint connections use joint polygons for gap filling, so width maintenance is only needed for mid-point connections
  if (shouldMaintainWidth) {
    const wall = shape.wall;
    const wallVector = new THREE.Vector2().subVectors(wall.end, wall.start);
    if (wallVector.lengthSq() > 0) {
      const wallDirection = wallVector.clone().normalize();
      const perpendicular = new THREE.Vector2(-wallDirection.y, wallDirection.x);
      const wallWidth = wall.wallWidth || floorplannerStore.wallWidth;

      // CRITICAL: Maintain wall width by adjusting the opposite corner
      // When topLeft moves, topRight must move to maintain width
      // When topRight moves, topLeft must move to maintain width
      if (corner === 'topLeft') {
        const topRightCorner = getCornerOfShape(shape, 'topRight');
        if (topRightCorner.index >= 0) {
          const newTopRight = point.clone().sub(perpendicular.clone().multiplyScalar(wallWidth));
          updatePathPoint(shape, topRightCorner.index, newTopRight);
        }
      } else if (corner === 'topRight') {
        const topLeftCorner = getCornerOfShape(shape, 'topLeft');
        if (topLeftCorner.index >= 0) {
          const newTopLeft = point.clone().add(perpendicular.clone().multiplyScalar(wallWidth));
          updatePathPoint(shape, topLeftCorner.index, newTopLeft);
        }
      } else if (corner === 'bottomLeft') {
        const bottomRightCorner = getCornerOfShape(shape, 'bottomRight');
        if (bottomRightCorner.index >= 0) {
          const newBottomRight = point.clone().sub(perpendicular.clone().multiplyScalar(wallWidth));
          updatePathPoint(shape, bottomRightCorner.index, newBottomRight);
        }
      } else if (corner === 'bottomRight') {
        const bottomLeftCorner = getCornerOfShape(shape, 'bottomLeft');
        if (bottomLeftCorner.index >= 0) {
          const newBottomLeft = point.clone().add(perpendicular.clone().multiplyScalar(wallWidth));
          updatePathPoint(shape, bottomLeftCorner.index, newBottomLeft);
        }
      }
    }
  }

  if (tracking && tracking.collection) {
    tracking.collection.push({
      wallId: tracking.wallId,
      pathIndex: index,
      point: point.clone(),
    });
  }
};

const collectCandidateShapes = (
  primary: WallShapeType,
  otherShapes?: WallShapeType[],
): WallShapeType[] => {
  if (!otherShapes || otherShapes.length === 0) {
    return [primary];
  }

  const candidates: WallShapeType[] = [];
  const seen = new Set<WallShapeType>();

  const enqueue = (shape: WallShapeType) => {
    if (!shape || seen.has(shape)) {
      return;
    }
    seen.add(shape);
    candidates.push(shape);
  };

  enqueue(primary);
  otherShapes.forEach(shape => {
    if (shape.wall.id === primary.wall.id) {
      enqueue(shape);
    }
  });

  return candidates;
};

type CornerUpdateTracking = {
  wallId: string;
  collection: otherModifiedPath[];
};

const applyEndpointIntersectionsToShape = (
  wallShape: WallShapeType,
  endpoint: "start" | "end",
  intersections: { left: THREE.Vector2; right: THREE.Vector2 },
  tracking?: CornerUpdateTracking,
) => {
  // Wall direction: START is top, END is bottom (vertical wall)
  // We always trim the corners at the connected endpoint

  const recordCornerUpdate = (cornerIndex: number, position: THREE.Vector2) => {
    if (!tracking || cornerIndex < 0) {
      return;
    }
    tracking.collection.push({
      wallId: tracking.wallId,
      pathIndex: cornerIndex,
      point: position.clone(),
    });
  };

  if (endpoint === "start") {
    // START (top) endpoint - trim TOP corners
    const topLeftCorner = getCornerOfShape(wallShape, "topLeft");
    const topRightCorner = getCornerOfShape(wallShape, "topRight");

    if (topLeftCorner.index >= 0) {
      const leftPoint = intersections.left.clone();
      updatePathPoint(wallShape, topLeftCorner.index, leftPoint);
      recordCornerUpdate(topLeftCorner.index, leftPoint);
    }
    if (topRightCorner.index >= 0) {
      const rightPoint = intersections.right.clone();
      updatePathPoint(wallShape, topRightCorner.index, rightPoint);
      recordCornerUpdate(topRightCorner.index, rightPoint);
    }
  } else {
    // END (bottom) endpoint - trim BOTTOM corners
    const bottomLeftCorner = getCornerOfShape(wallShape, "bottomLeft");
    const bottomRightCorner = getCornerOfShape(wallShape, "bottomRight");

    if (bottomLeftCorner.index >= 0) {
      const leftPoint = intersections.left.clone();
      updatePathPoint(wallShape, bottomLeftCorner.index, leftPoint);
      recordCornerUpdate(bottomLeftCorner.index, leftPoint);
    }
    if (bottomRightCorner.index >= 0) {
      const rightPoint = intersections.right.clone();
      updatePathPoint(wallShape, bottomRightCorner.index, rightPoint);
      recordCornerUpdate(bottomRightCorner.index, rightPoint);
    }
  }
};


type ImaginaryIntersectionOptions = {
  sourceReferencePoint?: THREE.Vector2;
  targetReferencePoint?: THREE.Vector2;
  targetEndpoint?: "start" | "end";
};

const findImaginaryIntersectionForSide = (
  sourceShape: WallShapeType,
  side: "left" | "right",
  targets: WallShapeType[],
  options?: ImaginaryIntersectionOptions,
): THREE.Vector2 | undefined => {
  const sourceLines = calculateWallImaginaryLines(
    sourceShape.wall,
    sourceShape.wall.wallWidth ?? floorplannerStore.wallWidth,
  );
  const sourceLine = side === "left" ? sourceLines.left : sourceLines.right;

  let bestPoint: THREE.Vector2 | undefined;
  let bestScore = Infinity;

  targets.forEach(target => {
    if (!target?.wall) {
      return;
    }

    const targetLines = calculateWallImaginaryLines(
      target.wall,
      target.wall.wallWidth ?? floorplannerStore.wallWidth,
      undefined,
      false,
      options?.targetEndpoint,
    );

    [targetLines.left, targetLines.right].forEach(targetLine => {
      const intersection = intersectImaginaryLines(sourceLine, targetLine);
      if (!intersection) {
        return;
      }

      const sourceDist = options?.sourceReferencePoint
        ? intersection.point.distanceToSquared(options.sourceReferencePoint)
        : 0;
      const targetDist = options?.targetReferencePoint
        ? intersection.point.distanceToSquared(options.targetReferencePoint)
        : 0;

      // Prefer intersections close to the requested source reference point
      const score = sourceDist * 2 + targetDist;
      if (score < bestScore) {
        bestScore = score;
        bestPoint = intersection.point.clone();
      }
    });
  });

  return bestPoint;
};

const findClosestIntersectionPoint = (
  sourceStart: THREE.Vector2,
  sourceEnd: THREE.Vector2,
  targets: WallShapeType[],
  restrictToSegment: boolean,
): THREE.Vector2 | undefined => {
  let closestPoint: THREE.Vector2 | undefined;
  let minDistanceSq = Infinity;

  targets.forEach(target => {
    // Skip targets where wall is undefined or missing required properties
    if (!target.wall || !target.wall.start || !target.wall.end) {
      return;
    }

    // Extract wall geometry
    const wallStart = target.wall.start;
    const wallEnd = target.wall.end;
    const wallWidth = target.wall.wallWidth || floorplannerStore.wallWidth;

    // Calculate wall direction vector
    const wallDirection = new THREE.Vector2().subVectors(wallEnd, wallStart).normalize();
    
    // Check for zero-length wall
    if (wallDirection.lengthSq() < 1e-8) {
      return;
    }

    // Calculate perpendicular vector (following pattern in fillMeshWalls.ts line 32)
    const perpendicular = new THREE.Vector2(-wallDirection.y, wallDirection.x);
    
    // Calculate half width
    const halfWidth = wallWidth / 2;

    // Generate centerline points by extending far in both directions
    // Total line distance of 200000 units from -100000 to +100000
    const extensionDistance = 100000;
    const extendedStart = wallStart.clone().sub(wallDirection.clone().multiplyScalar(extensionDistance));
    const extendedEnd = wallEnd.clone().add(wallDirection.clone().multiplyScalar(extensionDistance));

    // Generate left outline: offset centerline by perpendicular * halfWidth
    const leftOutlineStart = extendedStart.clone().add(perpendicular.clone().multiplyScalar(halfWidth));
    const leftOutlineEnd = extendedEnd.clone().add(perpendicular.clone().multiplyScalar(halfWidth));

    // Generate right outline: offset centerline by perpendicular * -halfWidth
    const rightOutlineStart = extendedStart.clone().sub(perpendicular.clone().multiplyScalar(halfWidth));
    const rightOutlineEnd = extendedEnd.clone().sub(perpendicular.clone().multiplyScalar(halfWidth));

    // Test intersections against the two infinite lines
    const leftIntersection = checkLineIntersection(
      sourceStart,
      sourceEnd,
      leftOutlineStart,
      leftOutlineEnd,
      restrictToSegment,
    );

    if (leftIntersection.intersecting && leftIntersection.point) {
      const distanceSq = leftIntersection.point.distanceToSquared(sourceStart);
      if (distanceSq < minDistanceSq) {
        minDistanceSq = distanceSq;
        closestPoint = leftIntersection.point.clone();
      }
    }

    const rightIntersection = checkLineIntersection(
      sourceStart,
      sourceEnd,
      rightOutlineStart,
      rightOutlineEnd,
      restrictToSegment,
    );

    if (rightIntersection.intersecting && rightIntersection.point) {
      const distanceSq = rightIntersection.point.distanceToSquared(sourceStart);
      if (distanceSq < minDistanceSq) {
        minDistanceSq = distanceSq;
        closestPoint = rightIntersection.point.clone();
      }
    }
  });

  return closestPoint;
};

const shouldValidateShape = (shape: WallShapeType): boolean => {
  return canValidateRectangularWall(shape);
};

/**
 * Helper function to process mid-point trimming for a wall shape.
 * Handles intersection finding and corner updates for mid-point connections.
 *
 * @param wallShape - The wall shape to trim
 * @param connectedTo - The connected wall shape
 * @param otherShapes - Other wall shapes for candidate collection
 * @param cornersToUse - Which corners to update ("top" for start endpoint, "bottom" for end endpoint)
 * @returns Object containing whether trimming occurred and clipping polygon flag
 */
const processMidPointTrimming = (
  wallShape: WallShapeType,
  connectedTo: WallShapeType,
  otherShapes: WallShapeType[] | undefined,
  cornersToUse: "top" | "bottom",
  connection?: WallConnectionType,
): { trimmed: boolean; clip: boolean } => {
  const candidateShapes = collectCandidateShapes(connectedTo, otherShapes);
  let clip = false;
  let trimmed = false;

  const wall = wallShape.wall;

  const sourceReferencePoint = connection
    ? getConnectionPoint(wall.start, wall.end, connection.sourcePosition)
    : cornersToUse === "top"
      ? wall.start.clone()
      : wall.end.clone();

  const targetReferencePoint = connection
    ? getConnectionPoint(connectedTo.wall.start, connectedTo.wall.end, connection.targetPosition)
    : new THREE.Vector2(
        (connectedTo.wall.start.x + connectedTo.wall.end.x) / 2,
        (connectedTo.wall.start.y + connectedTo.wall.end.y) / 2,
      );
  const targetEndpoint = connection
    ? connection.targetPosition === WallConnectionStart
      ? "start"
      : connection.targetPosition === WallConnectionEnd
        ? "end"
        : undefined
    : undefined;

  const leftIntersection = findImaginaryIntersectionForSide(
    wallShape,
    "left",
    candidateShapes,
    {
      sourceReferencePoint,
      targetReferencePoint,
      targetEndpoint,
    },
  );

  const rightIntersection = findImaginaryIntersectionForSide(
    wallShape,
    "right",
    candidateShapes,
    {
      sourceReferencePoint,
      targetReferencePoint,
      targetEndpoint,
    },
  );

  if (leftIntersection && rightIntersection) {
    // Both corners intersect - calculate center point and set both corners from center
    const wallVector = new THREE.Vector2().subVectors(wall.end, wall.start);
    const wallDirection = wallVector.normalize();
    const perpendicular = new THREE.Vector2(-wallDirection.y, wallDirection.x);
    const wallWidth = wall.wallWidth || floorplannerStore.wallWidth;

    const center = new THREE.Vector2().addVectors(leftIntersection, rightIntersection).multiplyScalar(0.5);

    if (cornersToUse === "top") {
      const topLeft = center.clone().add(perpendicular.clone().multiplyScalar(wallWidth / 2));
      const topRight = center.clone().sub(perpendicular.clone().multiplyScalar(wallWidth / 2));

      updatePathPoint(wallShape, getCornerOfShape(wallShape, "topLeft").index, topLeft);
      updatePathPoint(wallShape, getCornerOfShape(wallShape, "topRight").index, topRight);
    } else {
      const bottomLeft = center.clone().add(perpendicular.clone().multiplyScalar(wallWidth / 2));
      const bottomRight = center.clone().sub(perpendicular.clone().multiplyScalar(wallWidth / 2));

      updatePathPoint(wallShape, getCornerOfShape(wallShape, "bottomLeft").index, bottomLeft);
      updatePathPoint(wallShape, getCornerOfShape(wallShape, "bottomRight").index, bottomRight);
    }

    clip = true;
    trimmed = true;
  } else if (leftIntersection) {
    // Only left intersection found
    if (cornersToUse === "top") {
      setCornerPosition(wallShape, "topLeft", leftIntersection);
    } else {
      setCornerPosition(wallShape, "bottomLeft", leftIntersection);
    }
    clip = true;
    trimmed = true;
  } else if (rightIntersection) {
    // Only right intersection found
    if (cornersToUse === "top") {
      setCornerPosition(wallShape, "topRight", rightIntersection);
    } else {
      setCornerPosition(wallShape, "bottomRight", rightIntersection);
    }
    clip = true;
    trimmed = true;
  }

  return { trimmed, clip };
};

/**
 * Helper function to build a clipping polygon from wall shape corners.
 * Uses shape.corners if available for correct ordering, otherwise sorts by angle.
 * Ensures counter-clockwise winding order for valid polygon-clipping operations.
 *
 * @param wallShape - The wall shape to create clipping polygon from
 * @returns Array of Vector2 points in correct winding order (counter-clockwise)
 */
const buildClippingPolygonFromShape = (wallShape: WallShapeType): THREE.Vector2[] => {
  // Helper to validate corner coordinates are finite
  const isValidCorner = (point?: THREE.Vector2): point is THREE.Vector2 => {
    return point !== undefined &&
           Number.isFinite(point.x) &&
           Number.isFinite(point.y);
  };

  // Helper to calculate polygon area (shoelace formula)
  const calculateArea = (points: THREE.Vector2[]): number => {
    let area = 0;
    const n = points.length;
    for (let i = 0; i < n; i++) {
      const j = (i + 1) % n;
      area += points[i].x * points[j].y;
      area -= points[j].x * points[i].y;
    }
    return Math.abs(area) / 2;
  };

  // Helper to check if corners are distinct (not all at the same point)
  const areCornersDistinct = (points: THREE.Vector2[]): boolean => {
    if (points.length < 2) return false;
    const threshold = 0.001; // 1mm threshold for distinctness
    for (let i = 1; i < points.length; i++) {
      if (points[i].distanceTo(points[0]) > threshold) {
        return true;
      }
    }
    return false;
  };

  // Minimum area threshold for valid clipping polygon (same as fillMeshWalls.ts)
  const MIN_VALID_AREA = 0.0001;

  // First try to use named corners if available and complete (all 4 keys)
  if (wallShape.corners && Object.keys(wallShape.corners).length === 4) {
    const topLeft = wallShape.corners.topLeft?.point;
    const topRight = wallShape.corners.topRight?.point;
    const bottomRight = wallShape.corners.bottomRight?.point;
    const bottomLeft = wallShape.corners.bottomLeft?.point;

    if (topLeft && topRight && bottomRight && bottomLeft) {
      // Validate all corners have finite coordinates
      if (!isValidCorner(topLeft) || !isValidCorner(topRight) ||
          !isValidCorner(bottomRight) || !isValidCorner(bottomLeft)) {
        console.warn(`[trimIntersectingLines] Skipping clipping polygon for wall ${wallShape.wall.id}: invalid corner coordinates`);
        return [];
      }

      // Use named corners in consistent order: topLeft -> topRight -> bottomRight -> bottomLeft (counter-clockwise)
      const polygonPoints = [
        topLeft.clone(),
        topRight.clone(),
        bottomRight.clone(),
        bottomLeft.clone(),
      ];

      // Validate corners are distinct
      if (!areCornersDistinct(polygonPoints)) {
        console.warn(`[trimIntersectingLines] Skipping clipping polygon for wall ${wallShape.wall.id}: corners are not distinct`);
        return [];
      }

      // Validate polygon has sufficient area
      const area = calculateArea(polygonPoints);
      if (area < MIN_VALID_AREA) {
        console.warn(`[trimIntersectingLines] Skipping clipping polygon for wall ${wallShape.wall.id}: zero area (${area.toFixed(6)})`);
        return [];
      }

      // console.log(`      Using named corners for clipping polygon`);
      return polygonPoints;
    }
  }

  // Fallback: Compute consistent ordering from paths[0-3] with proper winding
  console.log(`      Falling back to computed ordering for clipping polygon`);
  if (wallShape.paths.length >= 4) {
    // Validate path coordinates are finite before creating points
    for (let i = 0; i < 4; i++) {
      if (!Number.isFinite(wallShape.paths[i].x) || !Number.isFinite(wallShape.paths[i].y)) {
        console.warn(`[trimIntersectingLines] Skipping clipping polygon for wall ${wallShape.wall.id}: invalid path coordinates`);
        return [];
      }
    }

    const points = [
      new THREE.Vector2(wallShape.paths[0].x, wallShape.paths[0].y),
      new THREE.Vector2(wallShape.paths[1].x, wallShape.paths[1].y),
      new THREE.Vector2(wallShape.paths[2].x, wallShape.paths[2].y),
      new THREE.Vector2(wallShape.paths[3].x, wallShape.paths[3].y),
    ];

    // Compute centroid
    const centroid = new THREE.Vector2();
    points.forEach(p => centroid.add(p));
    centroid.divideScalar(4);

    // Sort by angle around centroid (ensures consistent winding)
    const polygonPoints = points.slice().sort((a, b) => {
      const angleA = Math.atan2(a.y - centroid.y, a.x - centroid.x);
      const angleB = Math.atan2(b.y - centroid.y, b.x - centroid.x);
      return angleA - angleB;
    });

    // Validate corners are distinct
    if (!areCornersDistinct(polygonPoints)) {
      console.warn(`[trimIntersectingLines] Skipping clipping polygon for wall ${wallShape.wall.id}: corners are not distinct`);
      return [];
    }

    // Ensure counter-clockwise order (positive area)
    const area = 0.5 * polygonPoints.reduce((sum, p, i) => {
      const next = polygonPoints[(i + 1) % 4];
      return sum + (p.x * next.y - next.x * p.y);
    }, 0);

    if (area < 0) {
      polygonPoints.reverse();
    }

    // Validate polygon has sufficient area
    const finalArea = Math.abs(area);
    if (finalArea < MIN_VALID_AREA) {
      console.warn(`[trimIntersectingLines] Skipping clipping polygon for wall ${wallShape.wall.id}: zero area (${finalArea.toFixed(6)})`);
      return [];
    }

    return polygonPoints;
  }

  // Last resort: validate and use whatever paths are available
  if (wallShape.paths.length > 0) {
    // Validate path coordinates are finite
    for (const path of wallShape.paths) {
      if (!Number.isFinite(path.x) || !Number.isFinite(path.y)) {
        console.warn(`[trimIntersectingLines] Skipping clipping polygon for wall ${wallShape.wall.id}: invalid path coordinates in last resort`);
        return [];
      }
    }

    const polygonPoints = wallShape.paths.slice(0, 4).map(p => p.clone());

    // Validate polygon has sufficient area
    const finalArea = calculateArea(polygonPoints);
    if (finalArea < MIN_VALID_AREA) {
      console.warn(`[trimIntersectingLines] Skipping clipping polygon for wall ${wallShape.wall.id}: zero area in last resort (${finalArea.toFixed(6)})`);
      return [];
    }

    return polygonPoints;
  }

  // No valid geometry available
  console.warn(`[trimIntersectingLines] Skipping clipping polygon for wall ${wallShape.wall.id}: no valid geometry available`);
  return [];
};

/**
 * Helper function to add wall clipping polygon.
 * Adds a clipping polygon to the connected wall to prevent overlapping fills.
 *
 * @param wallShape - The wall shape to create clipping polygon from
 * @param connectedToWallId - The ID of the wall to clip
 */
const addWallClippingPolygon = (wallShape: WallShapeType, connectedToWallId: string): void => {
  const connectedToWall = floorplannerStore.findObjectId(connectedToWallId);
  if (connectedToWall) {
    const polygon = buildClippingPolygonFromShape(wallShape);

    // Skip if polygon validation failed (returned empty array)
    if (polygon.length === 0) {
      return;
    }

    const clippingPolygon: ClippingPolygonType = {
      belongsTo: wallShape.wall.id,
      polygon,
      objectType: "wall" as const,
    };
    // console.log(`      Clipping polygon created:`, {
    //   receivingWall: connectedToWall.id,
    //   belongsTo: wallShape.wall.id,
    //   cornerPoints: clippingPolygon.polygon.map(p => `(${p.x.toFixed(3)}, ${p.y.toFixed(3)})`),
    //   connectedWallFound: true,
    // });
    const success = renderStore.addWallClipping(connectedToWall.id, clippingPolygon);
    if (!success) {
      console.warn(`      Failed to add clipping polygon to renderStore`);
    }
  } else {
    console.warn(`      Connected wall not found: ${connectedToWallId}`);
  }
};

/**
 * Helper function to add bidirectional clipping polygons with logging.
 * Always adds forward clipping; adds reciprocal only if target shape provided.
 *
 * @param sourceShape - The source wall shape
 * @param targetWallId - The ID of the target wall to clip
 * @param targetShape - Optional target wall shape for reciprocal clipping
 * @param isMidPoint - Whether this is a mid-point connection (for logging)
 * @param shouldClip - Whether clipping should be applied (for mid-point conditional)
 */
const addBidirectionalClippings = (
  sourceShape: WallShapeType,
  targetWallId: string,
  targetShape?: WallShapeType,
  isMidPoint = false,
  shouldClip = true,
): void => {
  if (!shouldClip) return;

  // Always add forward clipping
  // console.log(`    Adding forward clipping polygon from ${sourceShape.wall.id} to ${targetWallId}${isMidPoint ? ' (mid-point)' : ''}`);
  addWallClippingPolygon(sourceShape, targetWallId);

  // Add reciprocal only if target shape available
  if (targetShape) {
    // console.log(`    Adding reciprocal clipping polygon from ${targetShape.wall.id} to ${sourceShape.wall.id}${isMidPoint ? ' (mid-point)' : ''}`);
    addWallClippingPolygon(targetShape, sourceShape.wall.id);
  } else {
    // console.log(`    Skipping reciprocal clipping: target shape not available`);
  }
};

type LineOrientation = "same" | "opposite";

const getLineCornerKey = (shape: WallShapeType, position: "start" | "end"): WallCornerKey => {
  return position === "start" ? "topLeft" : "topRight";
};

const getConnectionCornerKey = (
  lineShape: WallShapeType,
  orientation: LineOrientation,
  position: "start" | "end",
): WallCornerKey => {
  const useLeftSide =
    lineShape.lineSide === "left"
      ? orientation === "same"
      : orientation === "opposite";

  if (useLeftSide) {
    return position === "start" ? "topLeft" : "bottomLeft";
  }
  return position === "start" ? "topRight" : "bottomRight";
};

const trimLineAgainstWall = (
  lineShape: WallShapeType,
  targetShape: WallShapeType,
  lineEnd: "start" | "end",
  orientation: LineOrientation,
  restrictToSegment: boolean,
  explicitTargetStart?: THREE.Vector2,
  explicitTargetEnd?: THREE.Vector2,
): boolean => {
  const lineStartPosition = getCornerOfShape(lineShape, getLineCornerKey(lineShape, "start")).position;
  const lineEndPosition = getCornerOfShape(lineShape, getLineCornerKey(lineShape, "end")).position;

  // Use explicit target segment endpoints if provided (edge mode), otherwise use corner-based positions (legacy mode)
  const targetStartPosition = explicitTargetStart ?? getCornerOfShape(
    targetShape,
    getConnectionCornerKey(lineShape, orientation, "start"),
  ).position;
  const targetEndPosition = explicitTargetEnd ?? getCornerOfShape(
    targetShape,
    getConnectionCornerKey(lineShape, orientation, "end"),
  ).position;

  let intersectionPoint: THREE.Vector2 | undefined;

  if (restrictToSegment) {
    // For mid-point connections, use polygon-based intersection
    intersectionPoint = findClosestIntersectionPoint(
      lineStartPosition,
      lineEndPosition,
      [targetShape],
      true,
    );
  } else {
    // For endpoint connections, use corner-based intersection
    const intersection = checkLineIntersection(
      lineStartPosition,
      lineEndPosition,
      targetStartPosition,
      targetEndPosition,
      restrictToSegment,
    );
    if (intersection.intersecting && intersection.point) {
      intersectionPoint = intersection.point;
    }
  }

  if (intersectionPoint) {
    const cornerKey = getLineCornerKey(lineShape, lineEnd);
    setCornerPosition(lineShape, cornerKey, intersectionPoint);
    return true;
  }

  return false;
};

/**
 * Edge-based trimming for simplified wall geometry.
 * In edge mode, endpoint connections (sourcePosition === 0 or 10000) are skipped
 * because joint polygons and outline segments handle visual adjustments.
 * Mid-point connections (sourcePosition !== 0 and !== 10000) continue to use trimming.
 *
 * @param wallShapes - Wall shapes to trim
 * @param otherShapes - Connected wall shapes
 * @param otherModifiedPaths - Array to track modified paths
 * @param scene - Three.js scene for debug
 * @param geometryCache - Wall geometry cache
 * @returns true if any trimming occurred
 */
const trimIntersectingShapesEdgeBased = (
  wallShapes: WallShapeType[],
  otherShapes: WallShapeType[] | undefined,
  otherModifiedPaths: otherModifiedPath[],
  scene?: THREE.Scene,
  geometryCache?: WallGeometryCache,
): boolean => {
  let trimmed = false;
  for (let i = 0; i < wallShapes.length; i++) {
    const shapeWall = wallShapes[i].wall;
    
    // CRITICAL: Always get fresh wall from store to ensure we have current connection state
    // The shape's wall might be a stale shallow copy with old edgeConnections
    const wall = floorplannerStore.wallsMap.get(shapeWall.id) ?? shapeWall;

    // Merge edge connections with center connections
    // Filter duplicates by id and positions to avoid double-processing
    let connections = wall.centerConnections || [];

    if (wall.edgeConnections) {
      const edgeConnectionMapped = wall.edgeConnections.map(ec => ({
        id: ec.targetWallId,
        sourcePosition: ec.sourceEndpoint === "start" ? WallConnectionStart : 
                       ec.sourceEndpoint === "end" ? WallConnectionEnd : 
                       WallConnectionMiddle,
        targetPosition: ec.targetEndpoint === "start" ? WallConnectionStart : 
                       ec.targetEndpoint === "end" ? WallConnectionEnd : 
                       WallConnectionMiddle,
        mode: "edge" as const,
      }));

      // Merge edge connections with center connections, filtering duplicates
      connections = [
        ...(wall.centerConnections || []),
        ...edgeConnectionMapped,
      ].filter((conn, index, self) =>
        index === self.findIndex(c =>
          c.id === conn.id &&
          c.sourcePosition === conn.sourcePosition &&
          c.targetPosition === conn.targetPosition
        )
      );
    }

    // Iterate through all connections, not just start/end
    for (const connection of connections) {
      const connectedTo = otherShapes?.find(
        (w) => w.wall.id === connection.id,
      );

      if (isEndpointConnectionPosition(connection.sourcePosition)) {
        // Edge-mode endpoint fills stay as base rectangles. Joint polygons cover
        // only the gap; trimming here can paint one wall's fill into another.
        if (connection.mode === "edge") {
          continue;
        }

        const targetWall = floorplannerStore.wallsMap.get(connection.id);
        if (!targetWall) {
          continue;
        }

        const endpoint =
          connection.sourcePosition === WallConnectionStart ? "start" : "end";
        const intersectionPoints = getEndpointImaginaryIntersections(
          wall,
          endpoint,
          targetWall,
          floorplannerStore.wallWidth,
          {
            forceSharedEndpoint: isEndpointConnectionPosition(connection.targetPosition),
          },
        );

        if (intersectionPoints) {
          applyEndpointIntersectionsToShape(
            wallShapes[i],
            endpoint,
            intersectionPoints,
          );
          trimmed = true;
          floorplannerStore.updateWallCornerStateFromShape(wallShapes[i]);
        }

        if (
          isEndpointConnectionPosition(connection.targetPosition) &&
          connectedTo
        ) {
          const targetEndpoint =
            connection.targetPosition === WallConnectionStart ? "start" : "end";
          const reciprocalIntersections = getEndpointImaginaryIntersections(
            targetWall,
            targetEndpoint,
            wall,
            floorplannerStore.wallWidth,
            {
              forceSharedEndpoint: true,
            },
          );

          if (reciprocalIntersections) {
            applyEndpointIntersectionsToShape(
              connectedTo,
              targetEndpoint,
              reciprocalIntersections,
              {
                wallId: connectedTo.wall.id,
                collection: otherModifiedPaths,
              },
            );
            trimmed = true;
            floorplannerStore.updateWallCornerStateFromShape(connectedTo);
          }
        }

        // Do NOT add clipping - let joint polygons handle the gap
        continue;
      }

      if (connectedTo) {
        // Mid-point connection - but skip trimming if target is an endpoint (handled by joint polygons)

        // If target is an endpoint, skip trimming - use joint polygons instead
        if (isEndpointConnectionPosition(connection.targetPosition)) {
          // Do NOT add clipping - let joint polygons handle the gap
        } else {
          // Target is also middle - normal trimming applies
          const result = processMidPointTrimming(wallShapes[i], connectedTo, otherShapes, "top", connection);

          if (result.trimmed) {
            trimmed = true;
          }

          // Clip only if result.clip (mid-point specific)
          addBidirectionalClippings(wallShapes[i], connection.id, connectedTo, true, result.clip);
        }
      } else {
        // For mid-point connections without connectedTo, still add forward clipping if needed
        // This ensures clipping is created even when shapes are not immediately available
        warnMissingConnectedShapeOnce(wall.id, connection.id);
        // Always add forward clipping for mid-point even if connectedTo shape is missing
        // The clipping polygon is built from the source wall's shape, so we only need the target ID
        addWallClippingPolygon(wallShapes[i], connection.id);
      }
    }
  }

  wallShapes.forEach(shape => populateDefaultCorners(shape));
  otherShapes?.forEach(shape => populateDefaultCorners(shape));

  // Skip validation for endpoint connections (walls are always 4-point rectangles)
  // Only validate mid-point connection trimming if needed
  if (trimmed) {
    const defaultWallWidth = floorplannerStore.wallWidth;
    const mutatedConnectedWalls = new Set(otherModifiedPaths.map(mod => mod.wallId));
    wallShapes.forEach(shape => {
      if (shouldValidateShape(shape)) {
        assertValidWallGeometry(shape, "trimIntersectingShapes", {
          defaultWallWidth,
        });
      }
      floorplannerStore.updateWallCornerStateFromShape(shape);
    });
    otherShapes?.forEach(shape => {
      if (!mutatedConnectedWalls.has(shape.wall.id)) {
        return;
      }
      if (shouldValidateShape(shape)) {
        assertValidWallGeometry(shape, "trimIntersectingShapes_connected", {
          defaultWallWidth,
        });
      }
      floorplannerStore.updateWallCornerStateFromShape(shape);
    });
  }

  return trimmed;
};

/**
 * Trim intersecting shapes by cutting the paths of the wall shapes at the intersections
 * @param wallShapes
 * @param otherShapes
 * @returns
 */
const trimIntersectingShapesLegacy = (
  wallShapes: WallShapeType[],
  otherShapes: WallShapeType[] | undefined,
  otherModifiedPaths: otherModifiedPath[],
  scene?: THREE.Scene,
  geometryCache?: WallGeometryCache,
): boolean => {
  let trimmed = false;
  for (let i = 0; i < wallShapes.length; i++) {
    const wall = wallShapes[i].wall;
    // Check both center connections and edge connections for start endpoint
    // For edge connections, check BOTH when this wall is the source AND when it's the target
    let startConnection = wall.centerConnections?.find(
      (c) => c.sourcePosition === WallConnectionStart,
    );
    if (!startConnection && wall.edgeConnections) {
      const startEdgeConn = wall.edgeConnections!.find((ec) => 
        (ec.sourceWallId === wall.id && ec.sourceEndpoint === "start") ||
        (ec.targetWallId === wall.id && ec.targetEndpoint === "start")
      );
      if (startEdgeConn) {
        // Determine target endpoint based on which side of the connection this wall is on
        const conn = startEdgeConn!; // Type narrowing helper - we know it's defined in this if block
        const isSource = conn.sourceWallId === wall.id;
        const targetWallId = isSource ? conn.targetWallId : conn.sourceWallId;
        const targetEndpoint = isSource ? conn.targetEndpoint : conn.sourceEndpoint;
        startConnection = {
          id: targetWallId,
          sourcePosition: WallConnectionStart,
          targetPosition: targetEndpoint === "start" ? WallConnectionStart :
                         targetEndpoint === "end" ? WallConnectionEnd :
                         WallConnectionMiddle,
        };
      }
    }
    const startConnectedTo = otherShapes?.find(
      (w) => w.wall.id === startConnection?.id,
    );
    // Check both center connections and edge connections for end endpoint
    // For edge connections, check BOTH when this wall is the source AND when it's the target
    let endConnection = wall.centerConnections?.find(
      (c) => c.sourcePosition === WallConnectionEnd,
    );
    if (!endConnection && wall.edgeConnections) {
      const endEdgeConn = wall.edgeConnections!.find((ec) => 
        (ec.sourceWallId === wall.id && ec.sourceEndpoint === "end") ||
        (ec.targetWallId === wall.id && ec.targetEndpoint === "end")
      );
      if (endEdgeConn) {
        // Determine target endpoint based on which side of the connection this wall is on
        const conn = endEdgeConn!; // Type narrowing helper - we know it's defined in this if block
        const isSource = conn.sourceWallId === wall.id;
        const targetWallId = isSource ? conn.targetWallId : conn.sourceWallId;
        const targetEndpoint = isSource ? conn.targetEndpoint : conn.sourceEndpoint;
        endConnection = {
          id: targetWallId,
          sourcePosition: WallConnectionEnd,
          targetPosition: targetEndpoint === "start" ? WallConnectionStart :
                         targetEndpoint === "end" ? WallConnectionEnd :
                         WallConnectionMiddle,
        };
      }
    }
    const endConnectedTo = otherShapes?.find(
      (w) => w.wall.id === endConnection?.id,
    );
    if (startConnectedTo) {
      const connectedShape = startConnectedTo!; // Type narrowing helper - we know it's defined here
      if (startConnection?.targetPosition === WallConnectionEnd) {
        // End to end connections, we need to extend the intersection checking for hit on sharp corners (restrictToSegment = false)
        const intersection = checkLineIntersection(
          getCornerOfShape(wallShapes[i], "topLeft").position,
          getCornerOfShape(wallShapes[i], "bottomLeft").position,
          getCornerOfShape(connectedShape, "topLeft").position,
          getCornerOfShape(connectedShape, "bottomLeft").position,
          false,
        );
        if (intersection.intersecting && intersection.point) {
          const point = intersection.point!; // Type narrowing helper - we know it's defined here
          setCornerPosition(wallShapes[i], "topLeft", point);
          setCornerPosition(connectedShape, "bottomLeft", point, {
            wallId: connectedShape.wall.id,
            collection: otherModifiedPaths,
          });
          trimmed = true;
          // Debug draw a dot at the four endpoints being checked for intersection
          // const geometry1 = new THREE.CircleGeometry(0.01, 32);
          // const material1 = new THREE.MeshBasicMaterial({ color: 0x0000ff });
          // const circle1 = new THREE.Mesh(geometry1, material1);
          // circle1.position.set(intersection.point.x, intersection.point.y, 0.01);
          // scene?.add(circle1);
        }
      } else if (startConnection?.targetPosition === WallConnectionStart) {
        // MESSY CODE
        // End to start connections, we need to extend the intersection checking for hit on sharp corners (restrictToSegment = false)
        const intersection = checkLineIntersection(
          getCornerOfShape(wallShapes[i], "topRight").position,
          getCornerOfShape(wallShapes[i], "bottomRight").position,
          getCornerOfShape(connectedShape, "topLeft").position,
          getCornerOfShape(connectedShape, "bottomLeft").position,
          false,
        );
        if (intersection.intersecting && intersection.point) {
          const point = intersection.point!; // Type narrowing helper - we know it's defined here
          //console.log('Intersection detected 1', startConnection.sourcePosition);
          // // Debug draw a dot at the four endpoints being checked for intersection
          // const geometry1 = new THREE.CircleGeometry(0.01, 32);
          // const material1 = new THREE.MeshBasicMaterial({ color: 0x0000ff });
          // const circle1 = new THREE.Mesh(geometry1, material1);
          // circle1.position.set(wallShapes[i].paths[1].x, wallShapes[i].paths[1].y, 0.01);
          // scene?.add(circle1);
          // const geometry2 = new THREE.CircleGeometry(0.01, 32);
          // const material2 = new THREE.MeshBasicMaterial({ color: 0x00ff00 });
          // const circle2 = new THREE.Mesh(geometry2, material2);
          // circle2.position.set(wallShapes[i].paths[2].x, wallShapes[i].paths[2].y, 0.01);
          // scene?.add(circle2);
          // const geometry3 = new THREE.CircleGeometry(0.01, 32);
          // const material3 = new THREE.MeshBasicMaterial({ color: 0xff00ff });
          // const circle3 = new THREE.Mesh(geometry3, material3);
          // circle3.position.set(startConnectedTo.paths[0].x, startConnectedTo.paths[0].y, 0.01);
          // scene?.add(circle3);
          // const geometry4 = new THREE.CircleGeometry(0.01, 32);
          // const material4 = new THREE.MeshBasicMaterial({ color: 0xffff00 });
          // const circle4 = new THREE.Mesh(geometry4, material4);
          // circle4.position.set(startConnectedTo.paths[3].x, startConnectedTo.paths[3].y, 0.01);
          // scene?.add(circle4);

          // // Debug draw the intersection point
          // const geometry5 = new THREE.CircleGeometry(0.01, 32);
          // const material5 = new THREE.MeshBasicMaterial({ color: 0x000000 });
          // const circle5 = new THREE.Mesh(geometry5, material5);
          // circle5.position.set(intersection.point?.x || 0, intersection.point?.y || 0, 0.01);
          // scene?.add(circle5);

          setCornerPosition(wallShapes[i], "topRight", point);
          setCornerPosition(connectedShape, "topLeft", point, {
            wallId: connectedShape.wall.id,
            collection: otherModifiedPaths,
          });
          trimmed = true;
        }
      } else {
        // End to middle connections - use shared trimming helper
        const result = processMidPointTrimming(wallShapes[i], connectedShape, otherShapes, "top");

        if (result.trimmed) {
          trimmed = true;
        }

        if (result.clip) {
          addBidirectionalClippings(wallShapes[i], startConnection!.id, connectedShape, true, true);
        }
      }
    }
    if (endConnectedTo) {
      const endConnectedShape = endConnectedTo!; // Type narrowing helper - we know it's defined here
      if (endConnection?.targetPosition === WallConnectionStart) {
        // End to end connections, we need to extend the intersection checking for hit on sharp corners (restrictToSegment = false)
        const intersection = checkLineIntersection(
          getCornerOfShape(wallShapes[i], "topRight").position, //wallShapes[i].paths[1],
          getCornerOfShape(wallShapes[i], "bottomRight").position, //wallShapes[i].paths[2],
          getCornerOfShape(endConnectedShape, "topRight").position, //endConnectedTo.paths[1],
          getCornerOfShape(endConnectedShape, "bottomRight").position, //endConnectedTo.paths[2],
          false,
        );
        if (intersection.intersecting && intersection.point) {
          const point = intersection.point!; // Type narrowing helper - we know it's defined here
          //console.log('Intersection detected 3.0', point);
          setCornerPosition(wallShapes[i], "bottomRight", point);
          setCornerPosition(endConnectedShape, "topRight", point, {
            wallId: endConnectedShape.wall.id,
            collection: otherModifiedPaths,
          });
          trimmed = true;
        }
      } else if (endConnection?.targetPosition === WallConnectionEnd) {
        // End to start connections, we need to extend the intersection checking for hit on sharp corners (restrictToSegment = false)
        const intersection = checkLineIntersection(
          getCornerOfShape(wallShapes[i], "topLeft").position,
          getCornerOfShape(wallShapes[i], "bottomLeft").position,
          getCornerOfShape(endConnectedShape, "topRight").position,
          getCornerOfShape(endConnectedShape, "bottomRight").position,
          false,
        );
        if (intersection.intersecting && intersection.point) {
          const point = intersection.point!; // Type narrowing helper - we know it's defined here
          //console.log('Intersection detected 3.1', endConnection.sourcePosition);
          // // Debug draw a dot at the four endpoints being checked for intersection
          // const geometry1 = new THREE.CircleGeometry(0.01, 32);
          // const material1 = new THREE.MeshBasicMaterial({ color: 0x0000ff });
          // const circle1 = new THREE.Mesh(geometry1, material1);
          // circle1.position.set(wallShapes[i].paths[1].x, wallShapes[i].paths[1].y, 0.01);
          // scene?.add(circle1);
          // const geometry2 = new THREE.CircleGeometry(0.01, 32);
          // const material2 = new THREE.MeshBasicMaterial({ color: 0x00ff00 });
          // const circle2 = new THREE.Mesh(geometry2, material2);
          // circle2.position.set(wallShapes[i].paths[2].x, wallShapes[i].paths[2].y, 0.01);
          // scene?.add(circle2);
          // const geometry3 = new THREE.CircleGeometry(0.01, 32);
          // const material3 = new THREE.MeshBasicMaterial({ color: 0xff00ff });
          // const circle3 = new THREE.Mesh(geometry3, material3);
          // circle3.position.set(endConnectedTo.paths[0].x, endConnectedTo.paths[0].y, 0.01);
          // scene?.add(circle3);
          // const geometry4 = new THREE.CircleGeometry(0.01, 32);
          // const material4 = new THREE.MeshBasicMaterial({ color: 0xffff00 });
          // const circle4 = new THREE.Mesh(geometry4, material4);
          // circle4.position.set(endConnectedTo.paths[3].x, endConnectedTo.paths[3].y, 0.01);
          // scene?.add(circle4);

          // // Debug draw the intersection point
          // const geometry5 = new THREE.CircleGeometry(0.01, 32);
          // const material5 = new THREE.MeshBasicMaterial({ color: 0x000000 });
          // const circle5 = new THREE.Mesh(geometry5, material5);
          // circle5.position.set(intersection.point?.x || 0, intersection.point?.y || 0, 0.01);
          // scene?.add(circle5);


          setCornerPosition(wallShapes[i], "bottomLeft", point);
          setCornerPosition(endConnectedShape, "bottomRight", point, {
            wallId: endConnectedShape.wall.id,
            collection: otherModifiedPaths,
          });
          trimmed = true;
        }
      } else {
        // End to middle connections - use shared trimming helper
        const result = processMidPointTrimming(wallShapes[i], endConnectedShape, otherShapes, "bottom");

        if (result.trimmed) {
          trimmed = true;
        }

        if (result.clip) {
          addBidirectionalClippings(wallShapes[i], endConnection!.id, endConnectedShape, true, true);
        }
      }
    }
  }
  wallShapes.forEach(shape => populateDefaultCorners(shape));
  otherShapes?.forEach(shape => populateDefaultCorners(shape));
  if (trimmed) {
    // Skip validation for endpoint connections (walls are always 4-point rectangles)
    // Only validate mid-point connection trimming if needed
    // Note: This validation is kept for mid-point connections but skipped for endpoints
    {
      const defaultWallWidth = floorplannerStore.wallWidth;
      const mutatedConnectedWalls = new Set(otherModifiedPaths.map(mod => mod.wallId));
      wallShapes.forEach(shape => {
        if (shouldValidateShape(shape)) {
          assertValidWallGeometry(shape, "trimIntersectingShapes", {
            defaultWallWidth,
          });
        }
        floorplannerStore.updateWallCornerStateFromShape(shape);
      });
      otherShapes?.forEach(shape => {
        if (!mutatedConnectedWalls.has(shape.wall.id)) {
          return;
        }
        if (shouldValidateShape(shape)) {
          assertValidWallGeometry(shape, "trimIntersectingShapes_connected", {
            defaultWallWidth,
          });
        }
        floorplannerStore.updateWallCornerStateFromShape(shape);
      });
    }
  }
  return trimmed;
};

export const trimIntersectingShapes = (
  wallShapes: WallShapeType[],
  otherShapes: WallShapeType[] | undefined,
  otherModifiedPaths: otherModifiedPath[],
  scene?: THREE.Scene,
  geometryCache?: WallGeometryCache,
): boolean =>
  // Use simplified trimming that only handles mid-point connections.
  // Endpoint connections are handled by joint polygons.
  trimIntersectingShapesEdgeBased(
    wallShapes,
    otherShapes,
    otherModifiedPaths,
    scene,
    geometryCache,
  );

export const trimIntersectingLines = (
  lineShapes: WallShapeType[],
  wallShapes: WallShapeType[],
  geometryCache?: WallGeometryCache,
): boolean => {
  let trimmed = false;
  for (let i = 0; i < lineShapes.length; i++) {
    const shapeLine = lineShapes[i].wall;
    
    // CRITICAL: Always get fresh wall from store to ensure we have current connection state
    // The shape's wall might be a stale shallow copy with old edgeConnections
    const line = floorplannerStore.wallsMap.get(shapeLine.id) ?? shapeLine;

    // In edge mode, merge edge connections with center connections
    // Filter duplicates by id and positions to avoid double-processing
    let connections = line.centerConnections || [];

    if (line.edgeConnections) {
      const edgeConnectionMapped = line.edgeConnections.map(ec => ({
        id: ec.targetWallId,
        sourcePosition: ec.sourceEndpoint === "start" ? WallConnectionStart :
                       ec.sourceEndpoint === "end" ? WallConnectionEnd :
                       WallConnectionMiddle,
        targetPosition: ec.targetEndpoint === "start" ? WallConnectionStart :
                       ec.targetEndpoint === "end" ? WallConnectionEnd :
                       WallConnectionMiddle,
      }));

      // Merge edge connections with center connections, filtering duplicates
      connections = [
        ...(line.centerConnections || []),
        ...edgeConnectionMapped,
      ].filter((conn, index, self) =>
        index === self.findIndex(c =>
          c.id === conn.id &&
          c.sourcePosition === conn.sourcePosition &&
          c.targetPosition === conn.targetPosition
        )
      );
    }

    // Iterate through all connections, not just start/end
    for (const connection of connections) {
      // Skip endpoint trimming (handled by joint polygons)
      if (isEndpointConnectionPosition(connection.sourcePosition)) {
        continue;
      }

      const connectedTo = wallShapes.find(
        (w) => w.wall.id === connection.id,
      );

      if (connectedTo) {
        // Compute edge points if in edge mode and endpoint connection
        let explicitTargetStart: THREE.Vector2 | undefined;
        let explicitTargetEnd: THREE.Vector2 | undefined;

        if (isEndpointConnectionPosition(connection.sourcePosition) &&
            isEndpointConnectionPosition(connection.targetPosition)) {
          // Endpoint connection - use edge points
          const targetEndpoint = connection.targetPosition === WallConnectionStart ? "start" : "end";
          const targetWall = connectedTo.wall;
          const sourceWall = line;

          // Calculate edge points for the target wall
          const sourceAngle = getWallAngle(sourceWall);
          const targetEdgePoint = calculateWallEdgePoint(targetWall, targetEndpoint, sourceAngle, targetWall.wallWidth);

          // For edge-based trimming, we need to create a segment along the target wall edge
          // The segment should be perpendicular to the wall direction at the endpoint
          const targetWallDirection = new THREE.Vector2(
            targetWall.end.x - targetWall.start.x,
            targetWall.end.y - targetWall.start.y
          ).normalize();

          // Create a segment perpendicular to the wall direction (represents the wall edge)
          const perpendicular = new THREE.Vector2(-targetWallDirection.y, targetWallDirection.x);
          const segmentLength = (targetWall.wallWidth || floorplannerStore.wallWidth);

          explicitTargetStart = targetEdgePoint.edgePosition.clone().add(perpendicular.clone().multiplyScalar(segmentLength / 2));
          explicitTargetEnd = targetEdgePoint.edgePosition.clone().sub(perpendicular.clone().multiplyScalar(segmentLength / 2));
        }

        // Handle based on source connection position
        if (connection.sourcePosition === WallConnectionStart) {
          // Start endpoint connection
          if (connection.targetPosition === WallConnectionStart) {
            trimmed = trimLineAgainstWall(lineShapes[i], connectedTo, "start", "same", false, explicitTargetStart, explicitTargetEnd) || trimmed;
          } else if (connection.targetPosition === WallConnectionEnd) {
            trimmed = trimLineAgainstWall(lineShapes[i], connectedTo, "start", "opposite", false, explicitTargetStart, explicitTargetEnd) || trimmed;
          } else {
            // Mid-point on target - use polygon intersection
            trimmed = trimLineAgainstWall(lineShapes[i], connectedTo, "start", "same", true) || trimmed;
            trimmed = trimLineAgainstWall(lineShapes[i], connectedTo, "start", "opposite", true) || trimmed;
          }
        } else if (connection.sourcePosition === WallConnectionEnd) {
          // End endpoint connection
          if (connection.targetPosition === WallConnectionStart) {
            trimmed = trimLineAgainstWall(lineShapes[i], connectedTo, "end", "opposite", false, explicitTargetStart, explicitTargetEnd) || trimmed;
          } else if (connection.targetPosition === WallConnectionEnd) {
            trimmed = trimLineAgainstWall(lineShapes[i], connectedTo, "end", "same", false, explicitTargetStart, explicitTargetEnd) || trimmed;
          } else {
            // Mid-point on target - use polygon intersection
            trimmed = trimLineAgainstWall(lineShapes[i], connectedTo, "end", "same", true) || trimmed;
            trimmed = trimLineAgainstWall(lineShapes[i], connectedTo, "end", "opposite", true) || trimmed;
          }
        } else {
          // Mid-point connection on source - use polygon intersection
          // Determine which end is closer to the connection point
          const lineStart = line.start;
          const lineEnd = line.end;
          const connectionPoint = getConnectionPoint(lineStart, lineEnd, connection.sourcePosition);

          const distToStart = connectionPoint.distanceTo(lineStart);
          const distToEnd = connectionPoint.distanceTo(lineEnd);
          const lineEndToUse: "start" | "end" = distToStart <= distToEnd ? "start" : "end";

          if (connection.targetPosition === WallConnectionStart) {
            trimmed = trimLineAgainstWall(lineShapes[i], connectedTo, lineEndToUse, "same", false) || trimmed;
          } else if (connection.targetPosition === WallConnectionEnd) {
            trimmed = trimLineAgainstWall(lineShapes[i], connectedTo, lineEndToUse, "opposite", false) || trimmed;
          } else {
            // Mid-point on both
            trimmed = trimLineAgainstWall(lineShapes[i], connectedTo, lineEndToUse, "same", true) || trimmed;
            trimmed = trimLineAgainstWall(lineShapes[i], connectedTo, lineEndToUse, "opposite", true) || trimmed;
          }
        }
      }
    }
  }
  lineShapes.forEach(shape => populateDefaultCorners(shape));
  wallShapes.forEach(shape => populateDefaultCorners(shape));
  if (trimmed) {
    // Skip corner state updates for endpoint connections (handled by joint polygons)
    // Only update for mid-point connections
    // Note: Corner state updates are kept for mid-point connections but skipped for endpoints
    wallShapes.forEach(shape => floorplannerStore.updateWallCornerStateFromShape(shape));
  }
  return trimmed;
};

/**
 * Check if a wall connection is at an endpoint
 * @param connection - Wall connection to check
 * @returns true if connection is at an endpoint (sourcePosition === 0 or 10000)
 */
const isEndpointConnection = (connection: WallConnectionType | undefined): boolean => {
  if (!connection) {
    return false;
  }
  return isEndpointConnectionPosition(connection.sourcePosition);
};
